// /hr/lide — HR seznam všech osob (trenéři + ostatní pozice).
//
// RBAC (z briefu HR 22.5.2026):
//   owner   → vidí všechny + plný detail
//   manager → vidí všechny, plný detail jen u trenérů, u ostatních jen jméno+kontakty
//
// Layout:
//   1) Gradient header + tlačítko + Přidat osobu (owner only)
//   2) 4× KPI card (Celkem, Trenéři, Ostatní, Neaktivní)
//   3) Filtry: typ (Vše, Trenéři, Ostatní), stav a pozice
//   4) Fulltext search box
//   5) Tabulka (Foto, Jméno+Pozice, Email, Telefon, Typ spolupráce, Dokumenty, Status)
//   6) Klik = otevře HRPersonDetailModal
//
// Backend: /api/hr-list-people, /api/hr-get-person, /api/hr-create-person,
//          /api/hr-update-person, /api/hr-delete-person, /api/hr-photo, /api/hr-file.

import React, { useMemo, useState } from "react";
import { Briefcase, Users, UserCircle, RefreshCw, MapPin, Mail, Phone, FileText, ChevronRight, Plus, Lock } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { DlazdiceFiltr, PruhFiltru, Segment, FiltrRadek, EmptyState, SkeletonRows } from "../../components/ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { useApiData, apiFetch } from "../../lib/api.js";
import CopyButton from "../../components/CopyButton.jsx";
import { useDemoMode } from "../../lib/DemoModeContext.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import InfoTip from "../../components/InfoTip.jsx";
import HRPersonDetailModal from "../../components/HRPersonDetailModal.jsx";
import HRAddPersonModal from "../../components/HRAddPersonModal.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { toast } from "../../components/Toast.jsx";

// Pozice → barvy badge. Trenérské pozice fialové (jako Trenéři stránka),
// ostatní pozice barevně dle typu pro vizuální orientaci.
const POZICE_COLORS = {
  "CEO": "bg-amber-100 text-amber-700 border-amber-200",
  "Head Coach Senior": "bg-brand-200 text-brand-800 border-brand-300",
  "Head Coach Junior": "bg-brand-100 text-brand-700 border-brand-200",
  "Trener": "bg-brand-50 text-brand-700 border-brand-200",
  // 16.9.2026: trenéři najatí čistě na bootcampy, odměna za lekci.
  "BC trener": "bg-brand-100 text-brand-800 border-brand-200",
  "Nutricni specialista": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Clients Manager": "bg-sky-100 text-sky-700 border-sky-200",
  "Sales": "bg-rose-100 text-rose-700 border-rose-200",
  "Marketing": "bg-pink-100 text-pink-700 border-pink-200",
  "HR": "bg-orange-100 text-orange-700 border-orange-200",
  "IT": "bg-slate-200 text-slate-700 border-slate-300",
};
function poziceColor(p) {
  return POZICE_COLORS[p] || "bg-slate-100 text-slate-700 border-slate-200";
}
function poziceLabel(p) {
  if (p === "Trener") return "Trenér";
  if (p === "BC trener") return "Trenér bootcampů";
  if (p === "Nutricni specialista") return "Nutriční specialista";
  return p || "—";
}

function normalizeSearch(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}

// Dovolená v řádku člověka. Proužek nese všechny tři údaje: zelená je už
// vybráno, zlatá je schválené a naplánované, šedá je volný zbytek. Dřív to byla
// jedna dlouhá pilulka se čtyřmi čísly oddělenými čárkami a špatně se četla
// (14.9.2026, fáze 5d).
function SloupecDovolene({ dovolena, className = "" }) {
  const narok = Number(dovolena.narok) || 0;
  const podil = (x) => (narok > 0 ? Math.min(100, Math.round((Number(x) || 0) / narok * 100)) : 0);
  const dochazi = dovolena.zbyva <= 3;
  return (
    <div className={className} title={`Nárok ${narok} dní za rok ${dovolena.rok}, jeden den je 7 hodin`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">dovolená {dovolena.rok}</span>
        <span className={`text-sm font-bold tabular-nums ${dochazi ? "text-rose-700" : "text-slate-900"}`}>
          {dovolena.zbyva} z {narok}
        </span>
      </div>
      <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-slate-200">
        <span className="block h-full bg-emerald-500" style={{ width: `${podil(dovolena.cerpano)}%` }} />
        <span className="block h-full bg-brand-gold" style={{ width: `${podil(dovolena.naplanovano)}%` }} />
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1">
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-emerald-700">
          vybráno {dovolena.cerpano}
        </span>
        {dovolena.naplanovano > 0 && (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-amber-700">
            plán {dovolena.naplanovano}
          </span>
        )}
      </div>
    </div>
  );
}

export default function HRLide() {
  const user = getCurrentUser();
  const isOwner = user?.role === "owner";
  const { blurred } = useDemoMode();

  const [filter, setFilter] = useState("vse"); // vse | treneri | ostatni
  const [status, setStatus] = useState("aktivni"); // aktivni | neaktivni | vse
  const [selectedPozice, setSelectedPozice] = useState(""); // exact match
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedPersonInitial, setSelectedPersonInitial] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [togglePending, setTogglePending] = useState(null); // osoba, kterou prepiname Aktivni <-> Byvaly
  const [toggleBusy, setToggleBusy] = useState(false);

  async function doToggleStatus() {
    if (!togglePending) return;
    const p = togglePending;
    const newActive = !(p.active !== false); // toggle
    setToggleBusy(true);
    try {
      const resp = await apiFetch(`/api/hr-update-person?id=${encodeURIComponent(p.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newActive ? "Aktivní" : "Bývalý", active: newActive }),
      });
      if (!resp.ok) throw new Error(`server vrátil ${resp.status}`);
      setTogglePending(null);
      refetch();
    } catch (e) {
      toast("Změna statusu se nepovedla: " + (e?.message || e), "error");
    } finally {
      setToggleBusy(false);
    }
  }

  const { data, loading, error, refetch, refreshing } = useApiData(
    `/api/hr-list-people?filter=${filter}&status=${status}${selectedPozice ? `&pozice=${encodeURIComponent(selectedPozice)}` : ""}`
  );

  const people = data?.people || [];
  const counts = data?.counts || { treneri: 0, ostatni: 0, total: 0, neaktivni: 0 };
  const byPozice = data?.byPozice || {};

  // Client-side fulltext (po backend filtru)
  const visiblePeople = useMemo(() => {
    const q = normalizeSearch(searchQuery);
    if (!q) return people;
    return people.filter((p) => {
      const haystack = [p.display_name, p.email, p.telefon, p.pozice, p.lokalita].join(" ");
      return normalizeSearch(haystack).includes(q);
    });
  }, [people, searchQuery]);

  return (
    <div className={`mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6 ${blurred ? "demo-blur" : ""}`}>
      {/* Hlavička */}
      <GradientHeader
        title="Lidé"
        icon={Briefcase}
        napoveda="Seznam všech aktivních osob ve firmě. Trenéři, CEO, Head Coach Senior, Head Coach Junior, Clients Manager, Sales, Nutriční specialista a další. Owner vidí kompletní detail všech osob. Manager vidí kompletní detail u trenérů, u ostatních jen jméno a kontakty. Trenéři tady přístup nemají."
        akce={[
          ...(isOwner ? [{ klic: "pridat", ikona: Plus, popis: "Přidat osobu", onClick: () => setAddOpen(true) }] : []),
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
        ]}
      />

      {/* Čísla nahoře zároveň filtrují podle typu (rozhodnutí Č2). Čtvrtá
          dlaždice je jen číslo, dokumenty se nefiltrují. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <DlazdiceFiltr
          popisek="Celkem aktivních"
          hodnota={counts.total}
          ikona={Users}
          ton="znacka"
          zapnuto={filter === "vse"}
          onClick={() => setFilter("vse")}
        />
        <DlazdiceFiltr
          popisek="Trenéři"
          hodnota={counts.treneri}
          ikona={UserCircle}
          ton="znacka"
          info={<InfoTip text="Zahrnuje Head Coach Senior a Junior." />}
          zapnuto={filter === "treneri"}
          onClick={() => setFilter("treneri")}
        />
        <DlazdiceFiltr
          popisek="Ostatní pozice"
          hodnota={counts.ostatni}
          ikona={Briefcase}
          ton="ok"
          info={<InfoTip text="CEO, Sales, Clients Manager a další." />}
          zapnuto={filter === "ostatni"}
          onClick={() => setFilter("ostatni")}
        />
        <DlazdiceFiltr
          popisek="S dokumenty"
          hodnota={people.filter((p) => (p.files_count?.total || 0) > 0).length}
          ikona={FileText}
          ton="pozor"
          info={<InfoTip text="Osoby, které mají nahraný alespoň jeden soubor." />}
        />
      </div>

      {/* Filtry ze vzorníku (rozhodnutí F1). */}
      <PruhFiltru
        zapnuto={status !== "aktivni" || !!selectedPozice || !!searchQuery.trim()}
        onZrusit={() => { setStatus("aktivni"); setSelectedPozice(""); setSearchQuery(""); }}
      >
        <Segment
          aria-label="Stav osoby"
          value={status}
          onChange={setStatus}
          items={[
            { key: "aktivni", label: "Aktivní" },
            { key: "neaktivni", label: "Neaktivní" },
            { key: "vse", label: "Všichni" },
          ]}
        />
        {Object.keys(byPozice).length > 0 && (
          <FiltrRadek
            stitek="Pozice"
            vice={false}
            value={selectedPozice}
            onChange={(k) => setSelectedPozice(k === selectedPozice ? "" : k)}
            items={Object.entries(byPozice)
              .sort(([, a], [, b]) => b - a)
              .map(([poz, pocet]) => ({ key: poz, label: `${poziceLabel(poz)} (${pocet})` }))}
          />
        )}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Hledat jméno, email, telefon, pozici…"
          className="field w-full"
        />
        <div className="text-xs text-slate-500">
          Zobrazeno <b className="tabular-nums text-slate-800">{visiblePeople.length}</b> z <b className="tabular-nums text-slate-800">{people.length}</b>
        </div>
      </PruhFiltru>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          <div className="font-medium">Chyba</div>
          <div className="text-xs mt-1">{error}</div>
        </div>
      )}

      {loading && <SkeletonRows rows={6} />}

      {!loading && visiblePeople.length === 0 && (
        <div className="glass rounded-xl">
          <EmptyState
            icon={Users}
            title="Nikdo neodpovídá filtru"
            hint="Zkus zrušit pozici nebo přepnout stav na Všichni."
            action={<button type="button" className="btn btn-secondary" onClick={() => { setStatus("aktivni"); setSelectedPozice(""); setSearchQuery(""); setFilter("vse"); }}>Zrušit filtry</button>}
          />
        </div>
      )}

      {!loading && visiblePeople.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {visiblePeople.map((p) => (
            <PersonRow
              key={p.id}
              person={p}
              onClick={() => { setSelectedPersonId(p.id); setSelectedPersonInitial(p); }}
              onToggleStatus={isOwner ? () => setTogglePending(p) : undefined}
            />
          ))}
        </div>
      )}

      {/* Detail modal — instant render z initialData (řádek tabulky), background refresh */}
      {selectedPersonId && (
        <HRPersonDetailModal
          personId={selectedPersonId}
          initialData={selectedPersonInitial}
          onClose={() => { setSelectedPersonId(null); setSelectedPersonInitial(null); }}
          onChanged={() => { refetch(); }}
        />
      )}

      {/* Potvrzeni prepnuti statusu Aktivni <-> Byvaly (meni pristup + viditelnost) */}
      <ConfirmDialog
        open={!!togglePending}
        title={togglePending && togglePending.active === false ? "Označit jako aktivní?" : "Označit jako bývalý?"}
        description={
          togglePending && togglePending.active === false
            ? `Osoba "${togglePending?.display_name}" se vrátí mezi aktivní. Pokud je trenér, znovu se objeví v Report coaches a kapacitách, a pokud má účet, přístup se obnoví.`
            : `Osoba "${togglePending?.display_name}" se označí jako bývalá. Pokud je trenér, zmizí z Report coaches a kapacit, a pokud má účet, přestane mu fungovat přístup do aplikace.`
        }
        confirmLabel={togglePending && togglePending.active === false ? "Aktivovat" : "Označit bývalý"}
        variant={togglePending && togglePending.active === false ? "primary" : "danger"}
        onConfirm={doToggleStatus}
        onCancel={() => { if (!toggleBusy) setTogglePending(null); }}
      />

      {/* Add modal */}
      {addOpen && isOwner && (
        <HRAddPersonModal
          onClose={() => setAddOpen(false)}
          onCreated={(newId) => { setAddOpen(false); refetch(); setSelectedPersonId(newId); }}
        />
      )}
    </div>
  );
}

// ── Sub-komponenty ─────────────────────────────────────────────────────

function PersonRow({ person, onClick, onToggleStatus }) {
  const uzka = useUzkaObrazovka();
  const filesTotal = person.files_count?.total || 0;
  const isInactive = person.active === false;
  const statusPill = (
    <span
      role={onToggleStatus ? "button" : undefined}
      tabIndex={onToggleStatus ? 0 : undefined}
      onClick={onToggleStatus ? (e) => { e.stopPropagation(); onToggleStatus(); } : undefined}
      title={onToggleStatus ? "Kliknutím přepneš Aktivní / Bývalý" : undefined}
      className={`text-xs px-2 py-0.5 rounded-lg border font-semibold ${
        isInactive
          ? "bg-rose-100 text-rose-700 border-rose-300"
          : "bg-emerald-100 text-emerald-700 border-emerald-300"
      } ${onToggleStatus ? "cursor-pointer hover:brightness-95" : ""}`}
    >
      {isInactive ? "Bývalý" : "Aktivní"}
    </span>
  );
  // Telefon: fotka a jméno na první řádek, štítky pod ně, kontakt jako
  // vlastní řádky s celým textem. Nic se neořezává na tři písmena.
  if (uzka) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full border-b border-slate-100 px-4 py-3.5 text-left transition-colors last:border-b-0 ${
          isInactive ? "opacity-60 active:bg-slate-50" : "active:bg-brand-50/60"
        }`}
      >
        <div className="flex items-start gap-3">
          <PersonAvatar person={person} size={44} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{person.display_name}</span>
              {person.redacted && (
                <span title="Detail skrytý dle role" className="inline-flex shrink-0 items-center text-slate-400">
                  <Lock className="h-3.5 w-3.5" />
                </span>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {statusPill}
              <span className={`rounded-lg border px-2 py-0.5 text-xs font-medium ${poziceColor(person.pozice)}`}>
                {poziceLabel(person.pozice)}
              </span>
              {person.typ_spoluprace && (
                <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{person.typ_spoluprace}</span>
              )}
              {filesTotal > 0 && (
                <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                  <FileText className="h-3.5 w-3.5" /> {filesTotal}
                </span>
              )}
              {person.na_dovolene && (
                <span className="rounded-lg bg-sky-500 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">Na dovolené</span>
              )}
              {person.ma_narozeniny && (
                <span className="rounded-lg bg-rose-500 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">Narozeniny</span>
              )}
            </div>
          </div>
          <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-300" />
        </div>

        {(person.email || person.telefon || person.lokalita) && (
          <div className="mt-2 space-y-1">
            {person.email && (
              <div className="flex items-center gap-1.5 text-[13px] text-slate-700">
                <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate demo-sensitive">{person.email}</span>
                <CopyButton value={person.email} title="Zkopírovat email" />
              </div>
            )}
            {person.telefon && (
              <div className="flex items-center gap-1.5 text-[13px] text-slate-700">
                <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate demo-sensitive">{person.telefon}</span>
                <CopyButton value={person.telefon} title="Zkopírovat telefon" />
              </div>
            )}
            {person.lokalita && (
              <div className="flex items-center gap-1.5 text-[13px] text-slate-500">
                <MapPin className="h-4 w-4 shrink-0 text-slate-400" /> {person.lokalita}
              </div>
            )}
          </div>
        )}

        {person.dovolena && <SloupecDovolene dovolena={person.dovolena} className="mt-2" />}
      </button>
    );
  }

  return (
    /* 14.9.2026 (fáze 6a): div s onClick místo tlačítka. V řádku jsou tlačítka
       na zkopírování e-mailu a telefonu a tlačítko v tlačítku není platné HTML. */
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => { if (e.target.closest && e.target.closest("button, a")) return; onClick?.(); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      className={`w-full grid grid-cols-12 gap-3 items-center px-5 py-4 text-left cursor-pointer transition-all border-b border-slate-100 last:border-b-0 group ${
        isInactive ? "opacity-60 hover:bg-slate-50" : "hover:bg-brand-50/60 hover:shadow-sm"
      }`}
    >
      {/* Foto — větší (48px), gradient fallback per pozice */}
      <div className="col-span-1">
        <PersonAvatar person={person} size={48} />
      </div>
      {/* Jméno + pozice + lokalita */}
      <div className="col-span-3 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-slate-900 truncate demo-sensitive">{person.display_name}</span>
          {person.na_dovolene && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-sky-500 text-white flex-shrink-0" title="Právě teď má schválenou dovolenou">
              NA DOVOLENÉ
            </span>
          )}
          {person.ma_narozeniny && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-500 text-white flex-shrink-0" title="Dnes slaví narozeniny">
              NAROZENINY
            </span>
          )}
          {person.redacted && (
            <span title="Detail skrytý dle role" className="inline-flex items-center text-slate-400">
              <Lock className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {statusPill}
          <span className={`text-xs px-2 py-0.5 rounded-lg border font-medium ${poziceColor(person.pozice)}`}>
            {poziceLabel(person.pozice)}
          </span>
          {person.typ_spoluprace && (
            <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{person.typ_spoluprace}</span>
          )}
          {person.lokalita && (
            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {person.lokalita}
            </span>
          )}
        </div>
      </div>
      {/* Email */}
      <div className="col-span-2 min-w-0">
        {person.email ? (
          <div className="flex items-center gap-1.5 text-sm text-slate-700 truncate">
            <Mail className="w-4 h-4 shrink-0 text-slate-400" />
            <span className="truncate demo-sensitive">{person.email}</span>
            <CopyButton value={person.email} title="Zkopírovat email" />
          </div>
        ) : (
          <span className="text-xs text-slate-400 italic">bez emailu</span>
        )}
      </div>
      {/* Telefon */}
      <div className="col-span-2 min-w-0">
        {person.telefon ? (
          <div className="flex items-center gap-1.5 text-sm text-slate-700 truncate">
            <Phone className="w-4 h-4 shrink-0 text-slate-400" />
            <span className="truncate demo-sensitive">{person.telefon}</span>
            <CopyButton value={person.telefon} title="Zkopírovat telefon" />
          </div>
        ) : (
          <span className="text-xs text-slate-400 italic">—</span>
        )}
      </div>
      {/* Dovolená. Typ spolupráce se přesunul jako pilulka ke jménu, uvolněné
          místo dostala dovolená, která z jedné dlouhé pilulky vyrostla do
          proužku se třemi částmi (14.9.2026, fáze 5d). */}
      <div className="col-span-2">
        {person.dovolena
          ? <SloupecDovolene dovolena={person.dovolena} />
          : <span className="text-xs text-slate-300">—</span>}
      </div>
      {/* Dokumenty */}
      <div className="col-span-1 text-center">
        {filesTotal > 0 ? (
          <span className="inline-flex items-center gap-1 text-sm text-emerald-700 font-semibold bg-emerald-50 px-2 py-1 rounded">
            <FileText className="w-3.5 h-3.5" /> {filesTotal}
          </span>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </div>
      {/* Akce */}
      <div className="col-span-1 flex items-center justify-end">
        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-brand-500 transition-colors" />
      </div>
    </div>
  );
}

// Avatar — foto pokud existuje, jinak iniciály z jména.
// Gradient barva fallback per pozice (vizuální orientace v seznamu).
function PersonAvatar({ person, size = 40 }) {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = String(person.display_name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const sizeClass = size === 48 ? "w-12 h-12" : "w-10 h-10";
  const fontClass = size === 48 ? "text-sm" : "text-xs";
  const showImg = person.foto && !imgFailed;

  // Gradient barva per pozice (stejná logika jako poziceColor, ale gradient verze)
  const gradients = {
    "CEO": "from-amber-400 to-amber-600",
    "Head Coach Senior": "from-brand-500 to-brand-700",
    "Head Coach Junior": "from-brand-400 to-brand-600",
    "Trener": "from-brand-400 to-brand-500",
    "BC trener": "from-brand-500 to-brand-600",
    "Nutricni specialista": "from-emerald-400 to-emerald-600",
    "Clients Manager": "from-sky-400 to-sky-600",
    "Sales": "from-rose-400 to-rose-600",
    "Marketing": "from-pink-400 to-pink-600",
    "HR": "from-orange-400 to-orange-600",
    "IT": "from-slate-400 to-slate-600",
  };
  const gradient = gradients[person.pozice] || "from-slate-400 to-slate-600";

  // 🏖️ bublinka na kroužku, když má osoba právě teď schválenou dovolenou.
  // Výrazné provedení (17.7.): modrý ring kolem celé fotky + větší bublinka s bílým okrajem.
  const naDovolene = !!person.na_dovolene;
  const dovolenaBadge = naDovolene ? (
    <span
      title="Právě na dovolené"
      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 border-2 border-white flex items-center justify-center text-[13px] shadow-md"
    >🏖️</span>
  ) : null;
  const ringCls = naDovolene ? "ring-[3px] ring-sky-400" : "";
  const kruh = showImg ? (
    <img
      src={person.foto}
      alt={person.display_name}
      loading="lazy"
      className={`${sizeClass} rounded-full object-cover border-2 border-white shadow-sm ${naDovolene ? ringCls : "ring-1 ring-slate-200"}`}
      style={{ objectPosition: "50% 25%" }} // oblicej do stredu kruhu, 25% od horni hrany
      onError={() => setImgFailed(true)}
    />
  ) : (
    <div className={`${sizeClass} rounded-full bg-gradient-to-br ${gradient} text-white flex items-center justify-center font-bold ${fontClass} shadow-sm ${naDovolene ? ringCls : "ring-1 ring-white"}`}>
      {initials || "?"}
    </div>
  );
  if (!dovolenaBadge) return kruh;
  return <div className="relative inline-block flex-shrink-0">{kruh}{dovolenaBadge}</div>;
}
